import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiUser, FiArrowRight, FiSearch, FiTag } from 'react-icons/fi';
import SEO from '../components/SEO';

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const postsPerPage = 9;

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [currentPage, searchTerm, selectedCategory]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      let url = `https://shop.bailebelle.com/wp-json/wp/v2/posts?per_page=${postsPerPage}&page=${currentPage}&_embed`;
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      if (selectedCategory) {
        url += `&categories=${selectedCategory}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      const total = parseInt(response.headers.get('X-WP-Total') || '0');
      
      setBlogs(data || []);
      setTotalPages(Math.ceil(total / postsPerPage));
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Fallback to sample blogs
      setBlogs([
        {
          id: 1,
          title: { rendered: "The Art of Ballet: A Timeless Expression" },
          excerpt: { rendered: "Discover the beauty and grace of ballet as an art form that transcends time and culture." },
          date: "2024-01-15T10:00:00",
          author: 1,
          slug: "art-of-ballet",
          _embedded: {
            author: [{ name: "BaileBelle Team" }],
            "wp:featuredmedia": [{ source_url: "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=600&h=400&fit=crop" }],
            "wp:term": [[{ name: "Dance Tips", slug: "dance-tips" }]]
          }
        },
        {
          id: 2,
          title: { rendered: "Choosing the Perfect Dance Costume" },
          excerpt: { rendered: "Learn how to select the ideal costume that enhances your performance and reflects your artistic vision." },
          date: "2024-01-10T14:30:00",
          author: 1,
          slug: "choosing-perfect-dance-costume",
          _embedded: {
            author: [{ name: "BaileBelle Team" }],
            "wp:featuredmedia": [{ source_url: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=600&h=400&fit=crop" }],
            "wp:term": [[{ name: "Costumes", slug: "costumes" }]]
          }
        },
        {
          id: 3,
          title: { rendered: "Dance Jewelry: The Perfect Finishing Touch" },
          excerpt: { rendered: "Explore how the right jewelry can complete your dance ensemble and add elegance to your performance." },
          date: "2024-01-05T09:15:00",
          author: 1,
          slug: "dance-jewelry-perfect-finishing-touch",
          _embedded: {
            author: [{ name: "BaileBelle Team" }],
            "wp:featuredmedia": [{ source_url: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&h=400&fit=crop" }],
            "wp:term": [[{ name: "Jewelry", slug: "jewelry" }]]
          }
        }
      ]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('https://shop.bailebelle.com/wp-json/wp/v2/categories');
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([
        { id: 1, name: "Dance Tips", slug: "dance-tips" },
        { id: 2, name: "Costumes", slug: "costumes" },
        { id: 3, name: "Jewelry", slug: "jewelry" }
      ]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getTitle = (blog) => {
    if (!blog || !blog.title) return '';
    if (typeof blog.title === 'string') return blog.title;
    if (blog.title.rendered) return blog.title.rendered;
    return '';
  };

  const getExcerpt = (blog) => {
    if (!blog || !blog.excerpt) return '';
    if (typeof blog.excerpt === 'string') return blog.excerpt;
    if (blog.excerpt.rendered) return blog.excerpt.rendered;
    return '';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBlogs();
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  return (
    <>
      <SEO
        title="Blog | BaileBelle - Dance Tips, Trends & Insights"
        description="Discover the latest dance tips, trends, and insights from the BaileBelle community. Expert advice on ballet, costumes, and dance performance."
        keywords="dance blog, ballet tips, dance costumes, dance jewelry, dance performance, BaileBelle blog"
        url="/blog"
      />
      
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'serif' }}>
              BaileBelle Blog
            </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Discover the latest dance tips, trends, and insights from our community of dancers, instructors, and dance enthusiasts.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="mb-12">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search blog posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 bg-pink-500 text-black font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Search
                </button>
              </form>

              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <FiTag className="w-4 h-4 text-pink-500" />
                  <span className="text-white font-semibold">Categories:</span>
                </div>
                <button
                  onClick={() => handleCategoryChange('')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === '' 
                      ? 'bg-pink-500 text-black' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category.id.toString()
                        ? 'bg-pink-500 text-black'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
                {(searchTerm || selectedCategory) && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-pink-500 hover:text-pink-400 text-sm font-medium transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-700"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-4"></div>
                    <div className="h-3 bg-gray-700 rounded mb-2"></div>
                    <div className="h-3 bg-gray-700 rounded w-5/6"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : blogs.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogs.map((blog) => (
                  <article
                    key={blog.id}
                    className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden hover:bg-gray-900/70 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/10 group"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={
                          blog._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
                          'https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=600&h=400&fit=crop'
                        }
                        alt={stripHtml(getTitle(blog))}
                        className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
                        <div className="flex items-center space-x-1">
                          <FiCalendar className="w-4 h-4" />
                          <span>{formatDate(blog.date || blog.published_at || blog.created_at)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FiUser className="w-4 h-4" />
                          <span>{blog._embedded?.author?.[0]?.name || blog.author?.name || 'BaileBelle Team'}</span>
                        </div>
                      </div>

                      <h2 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-pink-400 transition-colors">
                        {stripHtml(getTitle(blog))}
                      </h2>

                      <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3">
                        {stripHtml(getExcerpt(blog))}
                      </p>

                      <Link
                        to={`/blog/${blog.slug}`}
                        className="inline-flex items-center text-pink-500 hover:text-pink-400 font-semibold text-sm transition-colors group/link"
                      >
                        Read More
                        <FiArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          currentPage === i + 1
                            ? 'bg-pink-500 text-black'
                            : 'bg-gray-800 text-white hover:bg-gray-700'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold text-white mb-4">No blog posts found</h3>
              <p className="text-gray-400 mb-6">
                {searchTerm || selectedCategory 
                  ? "Try adjusting your search criteria or filters."
                  : "Check back soon for new content!"
                }
              </p>
              {(searchTerm || selectedCategory) && (
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-pink-500 text-black font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
